// src/pages/customer/OrderDetails.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import ReturnRequestModal from '../../components/orders/ReturnRequestModal';
import { useOrders } from '../../contexts/OrdersContext';
import {
    ArrowLeft,
    Package,
    Truck,
    CheckCircle,
    Clock,
    MapPin,
    User,
    Phone,
    CreditCard,
    FileText,
    Download,
    Share2,
    Printer,
    Calendar,
    Hash,
    AlertCircle,
    Home,
    Building,
    XCircle
} from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';
import { formatDateTime, formatDate, getTimeAgo, formatDateTimeEnglish } from '../../utils/date';
import toast, { showToast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { formatPhoneNumber } from '../../utils/PhoneFormatter';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import ordersAPI from '../../api/orders';
import { CUSTOMER_CANCELLATION_REASONS } from '../../constants/cancellationReasons';

const OrderDetails: React.FC = () => {
    const { orderNumber } = useParams<{ orderNumber: string }>();
    const navigate = useNavigate();
    const { getOrder, downloadInvoice, trackOrder } = useOrders();
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [trackingData, setTrackingData] = useState<any>(null);
    const [cancelReason, setCancelReason] = useState('');
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReasonValue, setCancelReasonValue] = useState('customer_request');
    const [cancelReasonCustom, setCancelReasonCustom] = useState('');
    const [returnItem, setReturnItem] = useState<any | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const { user } = useAuth();
    const isStaff = user?.is_staff;


    useEffect(() => {
        const loadOrder = async () => {
            if (!orderNumber) return;

            setLoading(true);
            const orderData = await getOrder(orderNumber);
            if (orderData) {
                setOrder(orderData);

                // Load tracking data
                const tracking = await trackOrder(orderData.tracking_number ? orderData.tracking_number : '');
                setTrackingData(tracking);
            }
            setLoading(false);
        };

        loadOrder();
    }, [orderNumber]);

    const getStatusSteps = (status: string) => {
        const steps = [
            { id: 1, label: 'Confirmed', completed: ['confirmed', 'processing', 'packed', 'out_for_delivery', 'delivered'].includes(status) },
            { id: 2, label: 'Processing', completed: ['processing', 'packed', 'out_for_delivery', 'delivered'].includes(status) },
            { id: 3, label: 'Packed', completed: ['packed', 'out_for_delivery', 'delivered'].includes(status) },
            { id: 4, label: 'Out for Delivery', completed: ['out_for_delivery', 'delivered'].includes(status) },
            { id: 5, label: 'Delivered', completed: ['delivered'].includes(status) },
        ];
        return steps;
    };

    const handleShare = () => {
        const url = window.location.href;
        navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
    };

    const handlePrint = () => {
        window.print();
    };

    const handleCancelOrder = async () => {
        if (cancelReasonValue === 'other' && !cancelReasonCustom.trim()) {
            toast.error('Please describe the reason');
            return;
        }

        const reason = cancelReasonValue === 'other'
            ? cancelReasonCustom.trim()
            : CUSTOMER_CANCELLATION_REASONS.find(r => r.value === cancelReasonValue)?.label || cancelReasonValue;

        setIsCancelling(true);
        const response = await ordersAPI.cancelOrder(order.order_number, reason, cancelReasonValue);
        setIsCancelling(false);

        if (response.error) {
            toast.error(response.error);
            return;
        }

        setOrder(prev => ({
            ...prev,
            status: response.status,
        }));
        setShowCancelModal(false);
        setCancelReasonValue('customer_request');
        setCancelReasonCustom('');
        toast.success('Order cancelled');
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading order details...</p>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h1>
                    <p className="text-gray-600 mb-8">The order you're looking for doesn't exist or you don't have permission to view it.</p>
                    <button
                        onClick={() => navigate('/orders')}
                        className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700"
                    >
                        Back to My Orders
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/orders')}
                    className="flex items-center text-blue-600 hover:text-blue-700 font-medium mb-4"
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back to Orders
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {/* Left side */}
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Order Details</h1>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                            <span className="text-sm font-mono text-gray-600">#{order.order_number}</span>
                            {order.tracking_number && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-mono">
                                    {order.tracking_number}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Right side - buttons */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrint}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                            aria-label="Print"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => downloadInvoice(order.order_number)}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden xs:inline">Download Invoice</span>
                            <span className="xs:hidden">Invoice</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                {/* Left Column - Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Order Tracking */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Order Status</h2>

                        {/* Progress Steps */}
                        {order.status === 'cancelled' ? (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium text-red-900">Order Cancelled</p>
                                    <p className="text-sm text-red-700 mt-1">
                                        {order.cancellation_reason || 'This order has been cancelled.'}
                                    </p>
                                    {order.refund_status && (
                                        <p className="text-xs text-red-600 mt-2">
                                            Refund: {order.refund_status}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-5 gap-2">
                                {getStatusSteps(order.status).map((step, index) => (
                                    <div key={step.id} className="flex flex-col items-center">
                                        <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center mb-2 ${step.completed
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-400'
                                            }`}>
                                            {step.completed ? (
                                                <CheckCircle className="w-4 h-4" />
                                            ) : (
                                                <span className="text-sm">{step.id}</span>
                                            )}
                                        </div>
                                        <span className={`text-xs font-medium text-center ${step.completed ? 'text-gray-900' : 'text-gray-500'
                                            }`}>
                                            {step.label}
                                        </span>
                                    </div>
                                ))}

                            </div>
                        )}
                        {/* Status Info */}
                        <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Order Date</p>
                                    <p className="font-medium text-gray-900">{formatDateTimeEnglish(order.created_at)}</p>
                                </div>
                                {order.estimated_delivery && (
                                    <div>
                                        <p className="text-sm text-gray-500">Estimated Delivery</p>
                                        <p className="font-medium text-gray-900">{formatDateTimeEnglish(order.estimated_delivery)}</p>
                                    </div>
                                )}
                                {order.driver_name && (
                                    <div>
                                        <p className="text-sm text-gray-500">Driver</p>
                                        <p className="font-medium text-gray-900">{order.driver_name}</p>
                                    </div>
                                )}
                                {order.driver_contact && (
                                    <div>
                                        <p className="text-sm text-gray-500">Driver Contact</p>
                                        <p className="font-medium text-gray-900">{formatPhoneNumber(order.driver_contact, { showFlag: true })}</p>
                                    </div>
                                )}
                            </div>
                            {isStaff && order.driver_collected_at && (
                                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Truck className="w-4 h-4 text-amber-600" />
                                        <p className="text-sm font-medium text-amber-900">Package Collected</p>
                                    </div>
                                    <p className="text-xs text-amber-700">
                                        Collected on {formatDateTimeEnglish(order.driver_collected_at)}
                                    </p>
                                    {order.collection_notes && (
                                        <p className="text-xs text-amber-700 mt-1">
                                            Notes: {order.collection_notes}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Tracking Updates */}
                        {trackingData && (
                            <div className="mt-6">
                                <h3 className="font-medium text-gray-900 mb-3">Tracking Updates</h3>
                                <div className="space-y-3">
                                    {trackingData.updates?.map((update: any, index: number) => (
                                        <div key={index} className="flex items-start gap-3">
                                            <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                                            <div>
                                                <p className="text-sm text-gray-900">{update.description}</p>
                                                <p className="text-xs text-gray-500">{formatDateTimeEnglish(update.timestamp)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Items Ordered */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Items Ordered</h2>

                        <div className="space-y-4">
                            {order.items?.map((item: any) => (
                                <div key={item.id} className="flex flex-col sm:flex-row gap-4 p-4 border border-gray-100 rounded-lg">
                                    {/* Image + Title row on mobile */}<div className="flex gap-4">
                                        <Link
                                            to={`/product/${item.product_id}`}
                                            target="_blank"
                                            className="block w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 hover:opacity-80 transition-opacity"
                                        >
                                            {item.product_image ? (
                                                <img
                                                    src={item.product_image}
                                                    alt={item.product_name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <Package className="w-full h-full p-3 text-gray-400" />
                                            )}
                                        </Link>

                                        <div className="flex-1 sm:hidden">
                                            <Link
                                                to={`/product/${item.product_id}`}
                                                target="_blank"
                                                className="font-medium text-gray-900 hover:text-blue-600 transition-colors break-words"
                                            >
                                                {item.product_name}
                                            </Link>
                                            {item.product_variant_name && (
                                                <p className="text-xs text-gray-500 mt-0.5">{item.product_variant_name}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Details - stacks on mobile */}
                                    <div className="flex-1 min-w-0">
                                        {/* Desktop title */}
                                        <p className="font-medium text-gray-900 break-words hidden sm:block">{item.product_name}</p>
                                        {item.product_variant_name && (
                                            <p className="text-sm text-gray-600 mt-0.5 hidden sm:block">Variant: {item.product_variant_name}</p>
                                        )}

                                        {/* Bottom row: SKU + Quantity + Price on one line for mobile */}
                                        <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <p className="text-xs text-gray-500">SKU: {item.product_sku}</p>
                                                <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-gray-900 text-sm">{formatPrice(item.total_price)}</p>
                                                <p className="text-xs text-gray-400 hidden sm:block">{formatPrice(item.price_at_sale)} each</p>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1 sm:hidden">{formatPrice(item.price_at_sale)} each</p>
                                        {item.open_return_id ? (
                                            <Link
                                                to={`/returns/${item.open_return_id}`}
                                                className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                                            >
                                                View return →
                                            </Link>
                                        ) : item.can_be_returned ? (
                                            <button
                                                onClick={() => setReturnItem(item)}
                                                className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                                            >
                                                Request return
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Shipping Details */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Shipping Details</h2>

                        <div className="space-y-4">
                            {/* Address */}
                            <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    {/* Address Label */}
                                    {order.shipping_address_label && (
                                        <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full mb-2">
                                            {order.shipping_address_label}
                                        </span>
                                    )}
                                    <p className="font-medium text-gray-900">
                                        {order.shipping_house_number} {order.shipping_address_line_1}
                                    </p>
                                    {order.shipping_address_line_2 && (
                                        <p className="text-gray-600">{order.shipping_address_line_2}</p>
                                    )}
                                    <p className="text-gray-600">{order.shipping_city_name}</p>
                                </div>
                            </div>

                            {/* Contact Info Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Recipient */}
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gray-100 rounded-lg">
                                        <User className="w-4 h-4 text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Recipient</p>
                                        <p className="font-medium text-gray-900">{order.shipping_recipient_name}</p>
                                    </div>
                                </div>

                                {/* Primary Contact */}
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gray-100 rounded-lg">
                                        <Phone className="w-4 h-4 text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Primary Contact</p>
                                        <p className="font-medium text-gray-900">{formatPhoneNumber(order.shipping_contact_number)}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Secondary Contact - if exists */}
                            {order.shipping_contact_number_2 && (
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gray-100 rounded-lg w-10 flex justify-center">
                                        <Phone className="w-4 h-4 text-gray-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Secondary Contact</p>
                                        <p className="font-medium text-gray-900">{formatPhoneNumber(order.shipping_contact_number_2)}</p>
                                    </div>
                                </div>
                            )}

                            {/* Delivery Note */}
                            {order.shipping_delivery_note && (
                                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                    <p className="text-sm text-blue-800">
                                        <span className="font-medium">Delivery Note:</span> {order.shipping_delivery_note}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Summary & Actions */}
                <div className="space-y-6">
                    {/* Order Summary */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">Subtotal</span>
                                <span className="font-medium">{formatPrice(order.subtotal)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Delivery Fee</span>
                                <span className="font-medium">{formatPrice(order.delivery_fee)}</span>
                            </div>

                            {/* Add tax if your system has it */}
                            {order.tax && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Tax</span>
                                    <span className="font-medium">{formatPrice(order.tax)}</span>
                                </div>
                            )}

                            <div className="border-t border-gray-200 pt-3">
                                <div className="flex justify-between">
                                    <span className="text-lg font-bold text-gray-900">Total</span>
                                    <span className="text-lg font-bold text-gray-900">{formatPrice(order.total)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Payment Info - Updated to handle driver info */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-3 mb-4">
                                <CreditCard className="w-5 h-5 text-gray-600" />
                                <div>
                                    <p className="font-medium text-gray-900 capitalize">
                                        {order.payment_method.replace(/_/g, ' ')}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Status: <span className="capitalize">{order.payment_status}</span>
                                    </p>
                                </div>
                            </div>

                            {/* Driver Info if available */}
                            {order.driver && (
                                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                    <p className="text-sm font-medium text-gray-900 mb-1">Delivery Driver</p>
                                    <p className="text-sm text-gray-600">
                                        {order.driver.first_name} {order.driver.last_name}
                                    </p>
                                    {order.driver_contact && (
                                        <p className="text-sm text-gray-600 mt-1">{order.driver_contact}</p>
                                    )}
                                </div>
                            )}

                            {/* Cash payment details */}
                            {order.payment_method === 'cash_on_delivery' && order.status === 'delivered' && (
                                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Cash Received</span>
                                        <span className="font-medium">{formatPrice(order.cash_received || 0)}</span>
                                    </div>
                                    {order.cash_change && (
                                        <div className="flex justify-between text-sm mt-1">
                                            <span className="text-gray-600">Change Given</span>
                                            <span className="font-medium">{formatPrice(order.cash_change)}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Order Actions */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6">

                        <div className="space-y-3">
                            {(order.status === 'confirmed' || order.status === 'packed') && (
                                <button
                                    onClick={() => setShowCancelModal(true)}
                                    className="w-full px-4 py-2.5 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50"
                                >
                                    Cancel Order
                                </button>
                            )}

                            {order.status === 'out_for_delivery' && (
                                <button
                                    onClick={() => {/* Implement contact driver */ }}
                                    className="w-full px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700"
                                >
                                    Contact Driver
                                </button>
                            )}

                            <button
                                onClick={() => downloadInvoice(order.order_number)}
                                className="w-full px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                            >
                                <FileText className="w-4 h-4" />
                                Download Invoice
                            </button>
                        </div>
                    </div>

                    {/* Need Help? */}
                    <div className="bg-blue-50 rounded-2xl border border-blue-100 p-6">
                        <h3 className="font-bold text-gray-900 mb-3">Need Help?</h3>
                        <div className="space-y-2">
                            <button className="w-full text-left text-sm text-blue-700 hover:text-blue-800 p-2 hover:bg-blue-100 rounded-lg transition-colors">
                                Contact Support
                            </button>
                            <button className="w-full text-left text-sm text-blue-700 hover:text-blue-800 p-2 hover:bg-blue-100 rounded-lg transition-colors">
                                Report an Issue
                            </button>
                            <Link
                                to="/returns"
                                className="block w-full text-left text-sm text-blue-700 hover:text-blue-800 p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                                My Returns
                            </Link>
                            {/* <button className="w-full text-left text-sm text-blue-700 hover:text-blue-800 p-2 hover:bg-blue-100 rounded-lg transition-colors">
                                Return Request
                            </button> */}
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancelOrder}
                title="Cancel Order"
                message={
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                            All shipments on this order that haven't been delivered will be cancelled.
                            {order.status === 'packed' && ' Packed items will be returned to stock.'}
                        </p>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Reason
                            </label>
                            <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto">
                                {CUSTOMER_CANCELLATION_REASONS.map(r => (
                                    <button
                                        key={r.value}
                                        type="button"
                                        onClick={() => setCancelReasonValue(r.value)}
                                        className={`flex items-center gap-3 px-3 py-2.5 border rounded-lg text-left text-sm transition-colors ${cancelReasonValue === r.value
                                            ? 'border-red-500 bg-red-50 text-red-900'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        <span className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${cancelReasonValue === r.value
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}>
                                            {cancelReasonValue === r.value && (
                                                <span className="w-2 h-2 rounded-full bg-red-500" />
                                            )}
                                        </span>
                                        <span>{r.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {cancelReasonValue === 'other' && (
                            <input
                                type="text"
                                value={cancelReasonCustom}
                                onChange={(e) => setCancelReasonCustom(e.target.value)}
                                placeholder="Describe the reason"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            />
                        )}
                    </div>
                }
                confirmText={isCancelling ? 'Cancelling...' : 'Cancel Order'}
                cancelText="Keep Order"
                type="danger"
                isProcessing={isCancelling}
            />
            {returnItem && (
                <ReturnRequestModal
                    orderItem={returnItem}
                    onClose={() => setReturnItem(null)}
                    onSuccess={(rr) => {
                        setReturnItem(null);
                        navigate(`/returns/${rr.id}`);
                    }}
                />
            )}
        </div>
    );
};

export default OrderDetails;